import { observer } from "mobx-react-lite";
import { FC } from "react";

import { PresetButtonType } from "@bps/fluent-ui";
import {
  ContactStatus,
  ContactType
} from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import {
  categoriesItem,
  disciplinesItem,
  organisationEmployeesItem,
  searchFilterItem
} from "@modules/practice/screens/address-book/components/contacts-filters/contact-filter-items.ts";
import { ContactFilter } from "@shared-types/practice/contacts-filter.interface.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { FilterBar } from "@ui-components/filter-bar/FilterBar.tsx";
import {
  FilterBarProps,
  Item
} from "@ui-components/filter-bar/FilterBar.types.ts";
import { usePersistedHelper } from "@ui-components/hooks/usePersistedHelper.ts";

export type AdvancedSearchFilter = Pick<
  ContactFilter,
  | "search"
  | "statuses"
  | "categories"
  | "isHealthProvider"
  | "disciplines"
  | "organisationEmployees"
>;

export const defaultAdvancedSearchFilter: AdvancedSearchFilter = {
  categories: [],
  search: "",
  statuses: [ContactStatus.Active],
  disciplines: [],
  organisationEmployees: [],
  isHealthProvider: false
};

const tooltipText = {
  ORG: "organisations",
  PER: "providers"
};

export const AdvancedSearchListFilter: FC<
  Pick<FilterBarProps<AdvancedSearchFilter>, "children"> & {
    contactType: ContactType.Individual | ContactType.Organisation;
    presets?: PresetButtonType<AdvancedSearchFilter>[] | undefined;
  }
> = observer(({ contactType, presets, ...props }) => {
  const { practice } = useStores();

  const types: ContactType[] = [contactType];

  const initialState: AdvancedSearchFilter = usePersistedHelper(() => {
    return {
      ...defaultAdvancedSearchFilter,
      types
    };
  });

  const filterItems: Item<AdvancedSearchFilter>[] = [searchFilterItem()];

  if (contactType === ContactType.Organisation) {
    filterItems.push(
      categoriesItem(practice.ref.organisationCategories.keyTextValues)
    );
  }
  filterItems.push(
    disciplinesItem(practice.ref.contactDisciplines.keyTextValues)
  );

  if (contactType === ContactType.Individual) {
    filterItems.push(organisationEmployeesItem());
  }

  return (
    <FilterBar<AdvancedSearchFilter>
      {...props}
      items={filterItems}
      initialValues={initialState}
      presets={[
        {
          text: "Healthcare",
          name: "isHealthProvider",
          id: "isHealthProvider",
          iconName: "Hospital",
          tooltip: `Only show healthcare ${tooltipText[contactType]}`,
          tooltipPresses: `Only showing healthcare ${tooltipText[contactType]}`,
          valuesToBeSetOnToggleOn: {
            isHealthProvider: true
          },
          valuesToBeSetOnToggleOff: {
            isHealthProvider: false
          }
        }
      ]}
    />
  );
});
